import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

const SESSION_FLAG = "aj_session";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, obj = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Only probe the session endpoint if a login happened in this browser.
    // Avoids a guaranteed 401 console error on every anonymous page view.
    if (!localStorage.getItem(SESSION_FLAG)) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(SESSION_FLAG);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.otp_required) return data; // second factor pending — no session yet
    localStorage.setItem(SESSION_FLAG, "1");
    setUser(data);
    return data;
  };

  const verifyOtp = async (email, code) => {
    const { data } = await api.post("/auth/verify-otp", { email, code });
    localStorage.setItem(SESSION_FLAG, "1");
    setUser(data);
    return data;
  };

  const completeGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    localStorage.setItem(SESSION_FLAG, "1");
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem(SESSION_FLAG);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyOtp, completeGoogle, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export const ROLE_LABEL = {
  administrator: "Administrator",
  head_elder: "Head Elder",
  theological_reviewer: "Theological Reviewer",
  editor: "Editor",
  author: "Author",
  translator: "Translator",
};

export const PERMS = {
  administrator: new Set(["publish", "review", "translate", "edit", "author", "manage_users", "moderate"]),
  head_elder: new Set(["publish", "review", "edit", "author", "manage_users", "moderate"]),
  theological_reviewer: new Set(["review", "edit", "author", "moderate"]),
  editor: new Set(["publish", "edit", "author", "moderate"]),
  author: new Set(["author"]),
  translator: new Set(["translate", "author"]),
};

export function hasPerm(user, perm) {
  if (!user || !user.role) return false;
  return PERMS[user.role]?.has(perm) ?? false;
}
